import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, Spinner, Empty, Select, fmtNum } from "@/components/ui/Primitives";
import { api } from "@/lib/api";
import { Boxes, Search, X, MessageCircle, ChevronRight } from "lucide-react";

const CATS = [
  { k: "cx1", label: "CX1", full: "CX1" },
  { k: "cx2", label: "CX2", full: "CX2" },
  { k: "cx3", label: "CX3", full: "CX3" },
  { k: "cx3g", label: "CX3G", full: "CX3G" },
  { k: "cx4", label: "CX4", full: "CX4" },
];

const TIPOS_INFO = {
  amarela: { label: "Amarela", dot: "bg-yellow-400" },
  roxa: { label: "Roxa", dot: "bg-purple-500" },
  branca: { label: "Branca", dot: "bg-white border border-ink-300" },
  pelada: { label: "Pelada", dot: "bg-orange-200 border border-orange-400" },
};

const UNIDADES = ["Matriz VGS", "Araxá", "Ituporanga"];

export default function Stock() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fornecedor, setFornecedor] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [unidadeFilter, setUnidadeFilter] = useState("");
  const [q, setQ] = useState("");
  const [expandedInv, setExpandedInv] = useState(null);
  const [pallets, setPallets] = useState({}); // { invoice_id: [pallet, ...] }
  const [palletsLoading, setPalletsLoading] = useState({});

  const zerarEstoque = async (r, direto = false) => {
    const msg = direto
      ? `Zerar o saldo da NF #${r.nota_numero} (${fmtNum(r.total, 0)} cx)?`
      : `Nenhuma discrepância entre beneficiamento e pallets da NF #${r.nota_numero}.\n\n` +
        `Se essas ${fmtNum(r.total, 0)} cx NÃO existem mais fisicamente, deseja DAR BAIXA (zerar o saldo desta NF)?`;
    if (!window.confirm(msg)) return;
    const { data } = await api.post(`/invoices/${r.invoice_id}/zerar-estoque`);
    const tot = Object.values(data.aplicado || {}).reduce((a, b) => a + b, 0);
    alert(`✅ Baixa aplicada: ${fmtNum(tot, 0)} cx removidas do estoque da NF #${r.nota_numero}.`);
    window.location.reload();
  };

  const toggleExpand = async (invoiceId) => {
    if (expandedInv === invoiceId) { setExpandedInv(null); return; }
    setExpandedInv(invoiceId);
    if (pallets[invoiceId]) return; // já carregado
    setPalletsLoading((s) => ({ ...s, [invoiceId]: true }));
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/pallets`);
      // Só pallets com saldo em aberto (restante > 0)
      const abertos = (Array.isArray(data) ? data : []).filter((p) => {
        const rest = Number(p.restante ?? p.total_caixas ?? 0);
        return rest > 0 && p.status !== "expedido";
      });
      setPallets((s) => ({ ...s, [invoiceId]: abertos }));
    } catch (_) {
      setPallets((s) => ({ ...s, [invoiceId]: [] }));
    } finally {
      setPalletsLoading((s) => ({ ...s, [invoiceId]: false }));
    }
  };

  useEffect(() => {
    api.get("/stock").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const items = data?.breakdown || [];

  // Lista de fornecedores únicos do estoque (com saldo > 0)
  const fornecedores = useMemo(() => {
    const set = new Map();
    for (const it of items) {
      if ((it.total || 0) <= 0) continue;
      const nome = it.fornecedor || "—";
      set.set(nome, (set.get(nome) || 0) + it.total);
    }
    return Array.from(set.entries()).sort((a, b) => b[1] - a[1]);
  }, [items]);

  // Totais por unidade (todas as unidades, ignorando outros filtros — pra usar como resumo)
  const totaisPorUnidade = useMemo(() => {
    const m = {};
    for (const it of items) {
      if ((it.total || 0) <= 0) continue;
      const u = it.unidade || "Matriz VGS";
      m[u] = (m[u] || 0) + Number(it.total || 0);
    }
    return m;
  }, [items]);

  // Itens filtrados (fornecedor + tipo + unidade + busca)
  const filtered = useMemo(() => {
    return items.filter((it) => {
      if ((it.total || 0) <= 0) return false;
      if (fornecedor && it.fornecedor !== fornecedor) return false;
      if (tipoFilter && (it.tipo_cebola || "").toLowerCase() !== tipoFilter) return false;
      if (unidadeFilter && (it.unidade || "Matriz VGS") !== unidadeFilter) return false;
      if (q) {
        const s = q.toLowerCase();
        if (
          !(it.fornecedor || "").toLowerCase().includes(s) &&
          !String(it.nota_numero || "").includes(s)
        ) return false;
      }
      return true;
    });
  }, [items, fornecedor, tipoFilter, unidadeFilter, q]);

  // Matriz por tipo de cebola (respeitando filtros — importante!)
  const matriz = useMemo(() => {
    const rows = {};
    for (const it of filtered) {
      const t = (it.tipo_cebola || "amarela").toLowerCase();
      rows[t] = rows[t] || { tipo: t, cx1: 0, cx2: 0, cx3: 0, cx3g: 0, cx4: 0, total: 0, nfs: 0 };
      for (const c of CATS) rows[t][c.k] += Number(it[c.k] || 0);
      rows[t].total += Number(it.total || 0);
      rows[t].nfs += 1;
    }
    return Object.values(rows).sort((a, b) => b.total - a.total);
  }, [filtered]);

  // Totais gerais (respeitando filtros)
  const totalsFilt = useMemo(() => {
    const t = { cx1: 0, cx2: 0, cx3: 0, cx3g: 0, cx4: 0, total: 0 };
    for (const it of filtered) {
      for (const c of CATS) t[c.k] += Number(it[c.k] || 0);
      t.total += Number(it.total || 0);
    }
    return t;
  }, [filtered]);

  const clearFilters = () => { setFornecedor(""); setTipoFilter(""); setUnidadeFilter(""); setQ(""); };
  const hasFilters = fornecedor || tipoFilter || unidadeFilter || q;

  const [waPickerOpen, setWaPickerOpen] = useState(false);

  const shareWhatsApp = (unidadeAlvo) => {
    setWaPickerOpen(false);
    const now = new Date();
    const dataStr = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

    // Filtra itens conforme unidade escolhida (independente dos filtros da tela)
    const itensAlvo = items.filter((it) => {
      if ((it.total || 0) <= 0) return false;
      if (unidadeAlvo && (it.unidade || "Matriz VGS") !== unidadeAlvo) return false;
      return true;
    });

    // Agrupa por tipo de cebola
    const porTipo = {};
    for (const it of itensAlvo) {
      const t = (it.tipo_cebola || "amarela").toLowerCase();
      porTipo[t] = porTipo[t] || { cx1: 0, cx2: 0, cx3: 0, cx3g: 0, cx4: 0, total: 0 };
      for (const c of CATS) porTipo[t][c.k] += Number(it[c.k] || 0);
      porTipo[t].total += Number(it.total || 0);
    }

    const blocosTipo = Object.entries(porTipo)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([tipo, v]) => {
        const linhasCat = CATS
          .filter((c) => v[c.k] > 0)
          .map((c) => `*${c.label}*     ${fmtNum(v[c.k], 0)}`)
          .join("\n");
        const tipoLabel = (TIPOS_INFO[tipo]?.label || tipo).toUpperCase();
        return `*${tipoLabel}*  (${fmtNum(v.total, 0)} sc)\n\n${linhasCat}`;
      })
      .join("\n\n━━━━━━━━━━━━━━━━━━\n\n");

    const titulo = unidadeAlvo ? `ESTOQUE — ${unidadeAlvo.toUpperCase()}` : "ESTOQUE — TODAS AS UNIDADES";
    const corpo = Object.keys(porTipo).length === 0
      ? "_Nenhum saldo disponível no momento._"
      : blocosTipo;

    const msg =
`*${titulo}*
_Cebolas Serenata • ${dataStr}_

━━━━━━━━━━━━━━━━━━

${corpo}`;

    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) return <Layout title="Estoque"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;

  return (
    <Layout
      title="Estoque"
      subtitle="Caixas disponíveis em depósito"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              if (!window.confirm("Buscar e remover pallets órfãos (que apontam para NFs já excluídas)?")) return;
              try {
                const { data } = await api.post("/admin/limpar-pallets-orfaos");
                alert(`✅ ${data.pallets_removidos} pallet(s) órfão(s) removido(s) do estoque.`);
                window.location.reload();
              } catch (e) {
                alert("Erro: " + (e?.response?.data?.detail || e.message));
              }
            }}
            className="px-3 py-2 rounded-md bg-amber-100 text-amber-900 border border-amber-300 text-xs font-semibold hover:bg-amber-200 flex items-center gap-1.5"
            data-testid="limpar-pallets-orfaos"
            title="Remove pallets que ficaram no estoque após exclusão de NFs"
          >
            🧹 Limpar Órfãos
          </button>
          <button
            onClick={() => setWaPickerOpen(true)}
            className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 flex items-center gap-2"
            data-testid="share-whatsapp-stock"
          >
            <MessageCircle size={16} /> WhatsApp
          </button>
        </div>
      }
    >
      {/* Abas por unidade (rápido acesso) */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="stock-unidade-tabs">
        <button
          onClick={() => setUnidadeFilter("")}
          className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${!unidadeFilter ? "bg-brand-500 text-white" : "bg-white border border-ink-200 text-ink-700 hover:bg-stone-50"}`}
          data-testid="stock-tab-todas"
        >
          Todas as unidades · {fmtNum(Object.values(totaisPorUnidade).reduce((a, b) => a + b, 0), 0)} sc
        </button>
        {UNIDADES.map((u) => (
          <button
            key={u}
            onClick={() => setUnidadeFilter(u)}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${unidadeFilter === u ? "bg-brand-500 text-white" : "bg-white border border-ink-200 text-ink-700 hover:bg-stone-50"}`}
            data-testid={`stock-tab-${u.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {u} · {fmtNum(totaisPorUnidade[u] || 0, 0)} sc
          </button>
        ))}
      </div>

      {/* Filtros */}
      <Card className="p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Buscar (fornecedor ou NF)</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Nome ou número da NF..."
                className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                data-testid="stock-search-input"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <Select label="Unidade" value={unidadeFilter} onChange={(e) => setUnidadeFilter(e.target.value)} data-testid="stock-unidade-filter">
              <option value="">Todas</option>
              {UNIDADES.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </Select>
          </div>
          <div className="md:col-span-3">
            <Select label="Fornecedor" value={fornecedor} onChange={(e) => setFornecedor(e.target.value)} data-testid="stock-fornecedor-filter">
              <option value="">Todos ({fornecedores.length})</option>
              {fornecedores.map(([nome, tot]) => (
                <option key={nome} value={nome}>{nome} — {fmtNum(tot, 0)} sc</option>
              ))}
            </Select>
          </div>
          <div className="md:col-span-1">
            <Select label="Tipo" value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)} data-testid="stock-tipo-filter">
              <option value="">Todos</option>
              {Object.entries(TIPOS_INFO).map(([k, info]) => (
                <option key={k} value={k}>{info.label}</option>
              ))}
            </Select>
          </div>
          <div className="md:col-span-1 flex md:justify-end">
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="w-full md:w-auto inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold text-ink-500 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                data-testid="stock-clear-filters"
              >
                <X size={14} /> Limpar
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Totais gerais (respeita filtros) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-5">
        {CATS.map((c) => (
          <Card key={c.k} className="p-4" data-testid={`stock-${c.k}`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-500">{c.full}</div>
            <div className="font-mono text-2xl font-semibold text-ink-900 mt-1.5">{fmtNum(totalsFilt[c.k] || 0, 0)}</div>
            <div className="text-xs text-ink-500">caixas</div>
          </Card>
        ))}
        <div className="p-4 rounded-lg shadow-sm border bg-brand-500 text-white border-brand-500" data-testid="stock-total">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-100 flex items-center gap-1"><Boxes size={11} /> Total</div>
          <div className="font-mono text-2xl font-semibold mt-1.5">{fmtNum(totalsFilt.total || 0, 0)}</div>
          <div className="text-xs text-emerald-100">{fmtNum((totalsFilt.total || 0) * 20, 0)} kg</div>
        </div>
      </div>

      {/* Matriz por tipo de cebola */}
      {matriz.length > 0 && (
        <Card className="overflow-hidden mb-5">
          <div className="px-5 py-3 border-b border-ink-200 bg-stone-50">
            <h3 className="font-heading font-bold text-ink-900">Estoque por tipo de cebola</h3>
            <p className="text-xs text-ink-500">Detalhamento CX1..CX4 por variedade</p>
          </div>
          <table className="w-full text-sm" data-testid="stock-matriz">
            <thead className="bg-stone-50/50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Tipo</th>
                {CATS.map((c) => <th key={c.k} className="px-4 py-3 text-right">{c.label}</th>)}
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">NFs</th>
              </tr>
            </thead>
            <tbody>
              {matriz.map((r) => {
                const info = TIPOS_INFO[r.tipo] || { label: r.tipo, dot: "bg-ink-300" };
                return (
                  <tr key={r.tipo} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors" data-testid={`matriz-${r.tipo}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${info.dot}`} />
                        <span className="font-semibold capitalize">{info.label}</span>
                      </div>
                    </td>
                    {CATS.map((c) => (
                      <td key={c.k} className="px-4 py-3 font-mono text-right">{fmtNum(r[c.k] || 0, 0)}</td>
                    ))}
                    <td className="px-4 py-3 font-mono text-right font-semibold text-brand-600">{fmtNum(r.total, 0)}</td>
                    <td className="px-4 py-3 font-mono text-right text-ink-500">{r.nfs}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Detalhamento por NF */}
      {filtered.length === 0 ? (
        <Card><Empty title={hasFilters ? "Nenhum estoque encontrado com esses filtros" : "Estoque vazio"} hint={hasFilters ? "Ajuste ou limpe os filtros." : "Finalize beneficiamentos para popular o estoque."} /></Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-ink-200 bg-stone-50">
            <h3 className="font-heading font-bold text-ink-900">Detalhamento por NF</h3>
            <p className="text-xs text-ink-500">{filtered.length} nota(s) fiscal(is) com estoque</p>
          </div>
          <table className="w-full text-sm" data-testid="stock-detalhe">
            <thead className="bg-stone-50/50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">NF</th>
                <th className="px-4 py-3">Fornecedor</th>
                <th className="px-4 py-3">Unidade</th>
                <th className="px-4 py-3">Tipo</th>
                {CATS.map((c) => <th key={c.k} className="px-4 py-3 text-right">{c.label}</th>)}
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const tipo = (r.tipo_cebola || "").toLowerCase();
                const info = TIPOS_INFO[tipo] || { label: r.tipo_cebola || "—", dot: "bg-ink-300" };
                const isOpen = expandedInv === r.invoice_id;
                return (
                  <React.Fragment key={r.invoice_id}>
                    <tr
                      className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors cursor-pointer"
                      data-testid={`stock-row-${r.invoice_id}`}
                      onClick={() => toggleExpand(r.invoice_id)}
                    >
                      <td className="px-4 py-3">
                        <ChevronRight size={16} className={`text-ink-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      </td>
                      <td className="px-4 py-3 font-mono">#{r.nota_numero}</td>
                      <td className="px-4 py-3">{r.fornecedor}</td>
                      <td className="px-4 py-3 text-xs text-ink-700">{r.unidade || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${info.dot}`} />
                          <span className="capitalize">{info.label}</span>
                        </div>
                      </td>
                      {CATS.map((c) => (
                        <td key={c.k} className="px-4 py-3 font-mono text-right">{fmtNum(r[c.k] || 0, 0)}</td>
                      ))}
                      <td className="px-4 py-3 font-mono text-right font-semibold text-brand-600">{fmtNum(r.total, 0)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-stone-50/60" data-testid={`stock-pallets-${r.invoice_id}`}>
                        <td colSpan={6 + CATS.length} className="px-4 py-3">
                          {palletsLoading[r.invoice_id] ? (
                            <div className="flex items-center gap-2 text-sm text-ink-500 py-2">
                              <Spinner size={14} /> Carregando pallets...
                            </div>
                          ) : (pallets[r.invoice_id] || []).length === 0 ? (
                            <div className="py-2 flex items-center justify-between gap-3">
                              <div className="text-sm text-ink-500 italic">
                                Nenhum pallet em aberto para esta NF.
                                {Number(r.total || 0) > 0 && (
                                  <span className="ml-2 text-amber-800 not-italic font-semibold">
                                    ⚠ mas há {fmtNum(r.total, 0)} cx em estoque — recrie os pallets físicos ou baixe o estoque fantasma.
                                  </span>
                                )}
                              </div>
                              {Number(r.total || 0) > 0 && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const { data: diag } = await api.get(`/invoices/${r.invoice_id}/estoque-diagnostico`);
                                      if (!diag.tem_discrepancia) {
                                        await zerarEstoque(r);
                                        return;
                                      }
                                      const faltantes = Object.entries(diag.discrepancia).filter(([, v]) => Math.abs(v) > 0.01);
                                      const desc = faltantes
                                        .map(([c, v]) => `  ${c.toUpperCase()}: ${v > 0 ? "faltam" : "sobram"} ${Math.abs(v)} cx no sistema`)
                                        .join("\n");
                                      const escolha = window.prompt(
                                        `Discrepância detectada na NF #${diag.nota_numero}:\n\n${desc}\n\n` +
                                        `Escolha a ação (digite o número):\n\n` +
                                        `1 - CRIAR pallet(s) faltante(s) no sistema (as caixas existem fisicamente)\n` +
                                        `2 - BAIXAR do estoque (as caixas não existem — fantasmas)\n` +
                                        `3 - ZERAR todo o saldo desta NF (baixa manual)\n` +
                                        `Cancelar para sair.`,
                                        "1"
                                      );
                                      if (escolha === "1") {
                                        for (const [c, v] of faltantes) {
                                          if (v <= 0) continue;
                                          await api.post(`/invoices/${r.invoice_id}/recriar-pallet-faltante`, {
                                            categoria: c, quantidade: v,
                                            sacaria_tipo: "Genérica", sacaria_cor: "Amarela", ignorar_sacaria: false,
                                          });
                                        }
                                        alert("✅ Pallet(s) recriado(s) com sucesso.");
                                        window.location.reload();
                                      } else if (escolha === "2") {
                                        await api.post(`/invoices/${r.invoice_id}/reconciliar-estoque`);
                                        alert("✅ Estoque baixado com sucesso.");
                                        window.location.reload();
                                      } else if (escolha === "3") {
                                        await zerarEstoque(r, true);
                                      }
                                    } catch (err) {
                                      alert("Erro: " + (err.response?.data?.detail || err.message));
                                    }
                                  }}
                                  className="text-[11px] px-3 py-1.5 rounded bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200 font-semibold uppercase tracking-wider whitespace-nowrap"
                                  title="Recriar pallets faltantes ou baixar o estoque fantasma"
                                  data-testid={`reconciliar-${r.invoice_id}`}
                                >
                                  Reconciliar Estoque
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="py-1">
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-bold uppercase tracking-wider text-ink-500">
                                  Pallets em aberto ({(pallets[r.invoice_id] || []).length})
                                </div>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const { data: diag } = await api.get(`/invoices/${r.invoice_id}/estoque-diagnostico`);
                                      if (!diag.tem_discrepancia) {
                                        await zerarEstoque(r);
                                        return;
                                      }
                                      const faltantes = Object.entries(diag.discrepancia).filter(([, v]) => Math.abs(v) > 0.01);
                                      const desc = faltantes
                                        .map(([c, v]) => `  ${c.toUpperCase()}: ${v > 0 ? "faltam" : "sobram"} ${Math.abs(v)} cx no sistema`)
                                        .join("\n");
                                      const escolha = window.prompt(
                                        `Discrepância detectada na NF #${diag.nota_numero}:\n\n${desc}\n\n` +
                                        `Escolha a ação (digite o número):\n\n` +
                                        `1 - CRIAR pallet(s) faltante(s) no sistema (as caixas existem fisicamente)\n` +
                                        `2 - BAIXAR do estoque (as caixas não existem — fantasmas)\n` +
                                        `3 - ZERAR todo o saldo desta NF (baixa manual)\n` +
                                        `Cancelar para sair.`,
                                        "1"
                                      );
                                      if (escolha === "1") {
                                        for (const [c, v] of faltantes) {
                                          if (v <= 0) continue;
                                          await api.post(`/invoices/${r.invoice_id}/recriar-pallet-faltante`, {
                                            categoria: c, quantidade: v,
                                            sacaria_tipo: "Genérica", sacaria_cor: "Amarela", ignorar_sacaria: false,
                                          });
                                        }
                                        alert("✅ Pallet(s) recriado(s) com sucesso.");
                                        window.location.reload();
                                      } else if (escolha === "2") {
                                        await api.post(`/invoices/${r.invoice_id}/reconciliar-estoque`);
                                        alert("✅ Estoque baixado com sucesso.");
                                        window.location.reload();
                                      } else if (escolha === "3") {
                                        await zerarEstoque(r, true);
                                      }
                                    } catch (err) {
                                      alert("Erro: " + (err.response?.data?.detail || err.message));
                                    }
                                  }}
                                  className="text-[11px] px-2 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200 font-semibold uppercase tracking-wider"
                                  title="Compara beneficiamento vs. pallets físicos; ajusta o estoque se houver caixas fantasmas"
                                  data-testid={`reconciliar-${r.invoice_id}`}
                                >
                                  Reconciliar Estoque
                                </button>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {(pallets[r.invoice_id] || []).map((p) => {
                                  const cat = (p.itens?.[0]?.categoria || "").toUpperCase();
                                  const restante = Number(p.restante ?? p.total_caixas ?? 0);
                                  return (
                                    <Link
                                      key={p.id}
                                      to={`/pallets/${p.id}`}
                                      className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white border border-ink-200 hover:border-brand-400 hover:bg-brand-50/40 transition-colors"
                                      data-testid={`pallet-chip-${p.id}`}
                                    >
                                      <div className="flex flex-col min-w-0">
                                        <span className="font-mono font-semibold text-ink-900 text-sm truncate">{p.numero}</span>
                                        <span className="text-[10px] uppercase tracking-wider text-ink-500">{cat}</span>
                                      </div>
                                      <span className="font-mono font-bold text-brand-600 text-sm whitespace-nowrap">{fmtNum(restante, 0)} cx</span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal seletor de unidade para envio via WhatsApp */}
      {waPickerOpen && (
        <div
          className="fixed inset-0 z-50 bg-ink-900/60 flex items-center justify-center p-4"
          onClick={() => setWaPickerOpen(false)}
          data-testid="wa-picker-overlay"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="wa-picker-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-bold text-ink-900">Enviar estoque via WhatsApp</h3>
                <p className="text-xs text-ink-500 mt-0.5">Selecione qual unidade compartilhar:</p>
              </div>
              <button onClick={() => setWaPickerOpen(false)} className="p-1 hover:bg-stone-100 rounded" data-testid="wa-picker-close">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => shareWhatsApp("")}
                className="w-full text-left p-3 rounded-md border border-ink-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors flex items-center justify-between"
                data-testid="wa-pick-todas"
              >
                <div>
                  <div className="font-bold text-ink-900">Todas as unidades</div>
                  <div className="text-xs text-ink-500">Estoque consolidado geral</div>
                </div>
                <span className="font-mono font-bold text-emerald-700">
                  {fmtNum(Object.values(totaisPorUnidade).reduce((a, b) => a + b, 0), 0)} sc
                </span>
              </button>
              {UNIDADES.map((u) => (
                <button
                  key={u}
                  onClick={() => shareWhatsApp(u)}
                  className="w-full text-left p-3 rounded-md border border-ink-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors flex items-center justify-between"
                  data-testid={`wa-pick-${u.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div>
                    <div className="font-bold text-ink-900">{u}</div>
                    <div className="text-xs text-ink-500">Apenas esta unidade</div>
                  </div>
                  <span className="font-mono font-bold text-emerald-700">
                    {fmtNum(totaisPorUnidade[u] || 0, 0)} sc
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
